import React, {ReactNode} from 'react'
import styled from 'styled-components'
import {Box} from '@mui/material'
import {
    ApplyTitle,
    FirstPageDiv,
    HeaderArea,
    HeaderAreaBox,
    NextPageDiv
} from '../../../applications/common/admin/print/NatAdminApplicatioPrintCommonStyle'
import {getPrintDate} from '../../../applications/common/helper/NatApplicationHelper'
import {StyledDiv} from '../../../../../styledComponents/StyledTypeDefinitions'
import {convertToRoomNumberForResident, getFormattedZipCode} from '../../../../common/util/Natutil'
import {OwnershipTransferDetail} from '../../repository/NatGetOwnershipTransfer'
import {DO_OR_NOT, OWNERSHIP_TRANSFER_STATUS, RESIDENT_PLACE} from '../../../../../CodeMaster'
import {NatEncloseInParenthesis} from '../../../applications/common/admin/print/NatEncloseInParenthesis'

interface Props {
    ownershipTransferDocumentParam: OwnershipTransferDetail
}

export const NatOwnershipTransferDocument: React.FC<Props> = props => {
    const {
        ownershipTransferDocumentParam
    } = props

    const {
        applyId, condominiumName, ridgeName, roomNumber,
        intermediaryInfo, oldMemberInfo, newMemberInfo, applyHistoryInfo
    } = ownershipTransferDocumentParam

    if (!intermediaryInfo || !oldMemberInfo || !newMemberInfo || !applyHistoryInfo) {
        return <></>
    }

    const applicantRoom = `${ridgeName} ${convertToRoomNumberForResident(roomNumber)}`
    const oldMemberName = `${oldMemberInfo.oldMemberFamilyName} ${oldMemberInfo.oldMemberFirstName}`
    const newMemberName = `${newMemberInfo.newMemberFamilyName} ${newMemberInfo.newMemberFirstName}`
    const proxyContactName = `${newMemberInfo.proxyContactAddress.proxyContactFamilyName} ${newMemberInfo.proxyContactAddress.proxyContactFirstName}`
    const completedDate = applyHistoryInfo
        .find(history => history.applyStatus === OWNERSHIP_TRANSFER_STATUS.COMPLETE.value)?.registerDatetime ?? ''

    return (<>
        <FirstPageDiv>
            <HeaderArea>
                <HeaderAreaBox>
                    <Box ml={'auto'} mt={'10mm'}>申請ID：{applyId}</Box>
                </HeaderAreaBox>
                <HeaderAreaBox>
                    <Box ml={'auto'} mb={'4mm'}>{condominiumName}管理組合</Box>
                </HeaderAreaBox>
                <HeaderAreaBox>
                    <Box m={'auto'}><ApplyTitle>組合員資格変更届</ApplyTitle></Box>
                </HeaderAreaBox>
                <HeaderAreaBox>
                    <Box ml={'auto'}>{renderDate(completedDate)}</Box>
                </HeaderAreaBox>
            </HeaderArea>
            <Box ml={'25mm'} mt={'3mm'}>
                理事長殿
            </Box>
            <Box ml={'30mm'} mt={'5mm'} whiteSpace={'pre-wrap'}>
                1. 組合員資格の変更について届出をいたします。<br/>
                2. 新組合員は、規約（反社会的勢力の排除）に基づき、自らが反社会的勢力でないことを表明し、<br/>
                　その後も反社会勢力にならないことを確約いたします。<br/>
                3. 新組合員は、規約（管理費等の徴収）規定により、管理組合の指定する金融機関に管理費等の<br/>
                　支払口座を開設し、預金口座の振替手続きを行います。
            </Box>
            <Box margin={'3mm 0 0 30mm'} display={'flex'} flexDirection={'column'} width={'130mm'}>
                <Box>
                    <Box mb={'5mm'} whiteSpace={'pre-wrap'}>1 申請者</Box>
                    <Box ml={'8mm'} mb={'5mm'}>
                        {renderApplicationDetail('マンション名', condominiumName)}
                        {renderApplicationDetail('部屋番号', applicantRoom)}
                        {renderApplicationDetail('旧組合員氏名', oldMemberName)}
                        {renderApplicationDetail('新組合員氏名', newMemberName)}
                    </Box>
                </Box>
                <Box>
                    <Box mb={'5mm'}>
                        {renderApplicationDetail(
                            '2 組合員変更年月日', renderDate(intermediaryInfo.ownershipTransferDate)
                        )}
                    </Box>
                </Box>
                <Box>
                    <Box mb={'5mm'} whiteSpace={'pre-wrap'}>3 管理費等の支払変更期日</Box>
                    <Box ml={'8mm'} mb={'5mm'}>
                        {renderApplicationDetail(
                            '旧組合員', renderOldMemberPaymentChangeDate(intermediaryInfo.oldMemberPaymentChangeDate)
                        )}
                        {renderApplicationDetail(
                            '新組合員', renderNewMemberPaymentChangeDate(intermediaryInfo.newMemberPaymentChangeDate)
                        )}
                    </Box>
                </Box>
                <Box>
                    <Box mb={'5mm'} whiteSpace={'pre-wrap'}>4 入退去予定日</Box>
                    <Box ml={'8mm'} mb={'5mm'}>
                        {renderApplicationDetail('旧組合員', renderMoveOutDate(oldMemberInfo.moveOutDate))}
                        {renderApplicationDetail('新組合員', renderMoveInDate(newMemberInfo.moveInDate))}
                    </Box>
                </Box>
                <Box>
                    <Box mb={'5mm'}>
                        {renderApplicationDetail(
                            '5 新組合員の賃貸の有無', newMemberInfo.rentExistence === DO_OR_NOT.DO.value ? `有` : '無'
                        )}
                    </Box>
                </Box>
                <Box mb={'5mm'} whiteSpace={'pre-wrap'}>6 入退去後連絡先</Box>
                <Box ml={'8mm'}>旧組合員</Box>
                <Box ml={'12mm'} mb={'5mm'}>
                    {renderApplicationDetail('郵便番号', getFormattedZipCode(oldMemberInfo.zipCodeAfterMoveOut, false))}
                    {renderApplicationDetail('都道府県', oldMemberInfo.prefectureAfterMoveOut)}
                    {renderApplicationDetail('市区町村', oldMemberInfo.address2AfterMoveOut)}
                    {renderApplicationDetail('番地', oldMemberInfo.address3AfterMoveOut)}
                    {renderApplicationDetail('建物名･部屋番号', oldMemberInfo.buildingRoomNumberAfterMoveOut)}
                    {renderApplicationDetail('電話番号 ', oldMemberInfo.phoneNumberAfterMoveOut)}
                </Box>
            </Box>
        </FirstPageDiv>
        <NextPageDiv>
            <HeaderArea>
                <HeaderAreaBox>
                    <Box ml={'auto'} mt={'10mm'}>申請ID：{applyId}</Box>
                </HeaderAreaBox>
                <HeaderAreaBox>
                    <Box ml={'auto'} mb={'5mm'}>{condominiumName}管理組合</Box>
                </HeaderAreaBox>
                <HeaderAreaBox>
                    <Box m={'auto'}><ApplyTitle>組合員資格変更届</ApplyTitle></Box>
                </HeaderAreaBox>
            </HeaderArea>
            <Box margin={'2mm 0 0 30mm'} display={'flex'} flexDirection={'column'} width={'130mm'}>
                <Box>
                    <Box ml={'8mm'}>新組合員</Box>
                    {(newMemberInfo.residentPlace === RESIDENT_PLACE.FOREIGN.value) &&
                        <Box ml={'12mm'} mb={'5mm'}>
                            <PrintApplicationDetailContainer>
                                <Box>{'住所'}</Box>
                                <PrintApplicationParenthesisContainer>
                                    <StyledCardContent>
                                        {newMemberInfo.contactInformation.contactAddress}
                                    </StyledCardContent>
                                </PrintApplicationParenthesisContainer>
                            </PrintApplicationDetailContainer>
                            {renderApplicationDetail('電話番号 ', newMemberInfo.contactInformation.contactPhoneNumber)}
                        </Box>
                    }
                    {(newMemberInfo.residentPlace !== RESIDENT_PLACE.FOREIGN.value) &&
                        <Box ml={'12mm'} mb={'5mm'}>
                            {renderApplicationDetail(
                                '郵便番号', getFormattedZipCode(newMemberInfo.contactInformation.contactZipCode, false)
                            )}
                            {renderApplicationDetail('都道府県', newMemberInfo.contactInformation.contactPrefecture)}
                            {renderApplicationDetail('市区町村', newMemberInfo.contactInformation.contactAddress2)}
                            {renderApplicationDetail('番地', newMemberInfo.contactInformation.contactAddress3)}
                            {renderApplicationDetail(
                                '建物名･部屋番号', newMemberInfo.contactInformation.contactBuildingRoomNumber
                            )}
                            {renderApplicationDetail('電話番号 ', newMemberInfo.contactInformation.contactPhoneNumber)}
                        </Box>
                    }
                    <Box ml={'8mm'}>仲介業者</Box>
                    <Box ml={'12mm'} mb={'5mm'}>
                        {renderApplicationDetail('企業名', intermediaryInfo.companyName)}
                        {renderApplicationDetail(
                            '郵便番号', getFormattedZipCode(intermediaryInfo.intermediaryZipCode, false)
                        )}
                        {renderApplicationDetail('都道府県', intermediaryInfo.intermediaryPrefecture)}
                        {renderApplicationDetail('市区町村', intermediaryInfo.intermediaryAddress2)}
                        {renderApplicationDetail('番地', intermediaryInfo.intermediaryAddress3)}
                        {renderApplicationDetail('建物名･部屋番号', intermediaryInfo.intermediaryBuildingRoomNumber)}
                        {renderApplicationDetail('電話番号 ', intermediaryInfo.intermediaryPhoneNumber)}
                    </Box>
                </Box>
                {(newMemberInfo.residentPlace === RESIDENT_PLACE.FOREIGN.value) &&
                    <Box>
                        <Box mb={'5mm'} whiteSpace={'pre-wrap'}>7 新組合員が国外に居住する場合の国内連絡先</Box>
                        <Box ml={'12mm'}>
                            {renderApplicationDetail('氏名', proxyContactName)}
                            {renderApplicationDetail('続柄', newMemberInfo.proxyContactAddress.proxyContactRelationship)}
                            {renderApplicationDetail(
                                '郵便番号', getFormattedZipCode(
                                    newMemberInfo.proxyContactAddress.proxyContactZipCode, false
                                )
                            )}
                            {renderApplicationDetail('都道府県', newMemberInfo.proxyContactAddress.proxyContactPrefecture)}
                            {renderApplicationDetail('市区町村', newMemberInfo.proxyContactAddress.proxyContactAddress2)}
                            {renderApplicationDetail('番地', newMemberInfo.proxyContactAddress.proxyContactAddress3)}
                            {renderApplicationDetail(
                                '建物名･部屋番号', newMemberInfo.proxyContactAddress.proxyContactBuildingRoomNumber
                            )}
                            {renderApplicationDetail(
                                '電話番号 ', newMemberInfo.proxyContactAddress.proxyContactPhoneNumber
                            )}
                        </Box>
                    </Box>
                }
            </Box>
        </NextPageDiv>
    </>)

    NatOwnershipTransferDocument.displayName = 'NatOwnershipTransferDocument'
}

const renderApplicationDetail = (title: string, value: string | ReactNode) => {
    return (
        <PrintApplicationDetailContainer>
            <Box whiteSpace={'pre-wrap'}>{title}</Box>
            <NatEncloseInParenthesis item={value}/>
        </PrintApplicationDetailContainer>
    )
}

const renderDate = (date?: string) => {
    if (!date) {
        return <></>
    }

    const value = getPrintDate(date)

    return (
        <>
            <Box component={'span'} padding={'0 3mm 0 1mm'}>{value.year}</Box>年
            <Box component={'span'} padding={'0 4mm 0 4mm'}>{value.month}</Box>月
            <Box component={'span'} padding={'0 4mm 0 4mm'}>{value.day}</Box>日
        </>
    )
}

const renderOldMemberPaymentChangeDate = (date: string) => {
    const value = getPrintDate(date)

    return (
        <>
            <Box component={'span'} padding={'0 3mm 0 1mm'}>{value.year}</Box>年
            <Box component={'span'} padding={'0 4mm 0 4mm'}>{value.month}</Box>月
            <Box component={'span'} padding={'0 4mm 0 4mm'}>分まで支払い</Box>
        </>
    )
}

const renderNewMemberPaymentChangeDate = (date: string) => {
    const value = getPrintDate(date)

    return (
        <>
            <Box component={'span'} padding={'0 3mm 0 1mm'}>{value.year}</Box>年
            <Box component={'span'} padding={'0 4mm 0 4mm'}>{value.month}</Box>月
            <Box component={'span'} padding={'0 4mm 0 4mm'}>分から支払い</Box>
        </>
    )
}

const renderMoveOutDate = (date: string) => {
    const value = getPrintDate(date)

    return (
        <>
            <Box component={'span'} padding={'0 3mm 0 1mm'}>{value.year}</Box>年
            <Box component={'span'} padding={'0 4mm 0 4mm'}>{value.month}</Box>月
            <Box component={'span'} padding={'0 4mm 0 4mm'}>{value.day}</Box>日 退去
        </>
    )
}

const renderMoveInDate = (date: string) => {
    if (!date) {
        return <Box component={'span'} padding={'0 3mm 0 3mm'}>-</Box>
    }

    const value = getPrintDate(date)

    return (
        <>
            <Box component={'span'} padding={'0 3mm 0 1mm'}>{value.year}</Box>年
            <Box component={'span'} padding={'0 4mm 0 4mm'}>{value.month}</Box>月
            <Box component={'span'} padding={'0 4mm 0 4mm'}>{value.day}</Box>日 入居
        </>
    )
}

const PrintApplicationDetailContainer: StyledDiv = styled.div`
  display: flex;
  width: 100%;
`

const PrintApplicationParenthesisContainer: StyledDiv = styled.div`
  display: flex;
  height: 100%;
  width: 90mm;
  margin-left: auto;
`

const StyledCardContent = styled.div`
  border: solid 0.5pt black;
  width: 100%;
  min-height: 25mm;
  height: 100%;
  padding: 1mm 1mm 5mm 1mm;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  margin-bottom: 1mm;
`